import * as styles from "./SectionDogBio.module.scss";

import React, { useEffect } from "react";
import {
	shareOnFB,
	shareOnX,
	shareViaEmail,
	shareViaWhatsApp,
} from "../../../utils/socialMedia";

import { Link } from "gatsby";
import { Html5Entities } from "html-entities";
import QueryString from "qs";
import { renderToString } from "react-dom/server";
import DogBioLinks from "../../../components/DogBioLinks";
import FavouriteIcon from "../../../components/FavouriteIcon/FavouriteIcon";
import {
	Age,
	Breed,
	Home,
	Info,
	Location,
	Measurements,
	Medical,
	OffSite,
	Reference,
	Sex,
	Star,
} from "../../../components/common/Icons";
import { useDogSearchApi } from "../../../context";
import useWindowLocationHref from "../../../hooks/useWindowLocationHref";
import { Colours } from "../../../utils/colours";
import { getDogAgeBand } from "../../../utils/dates";
import { isSpecificSite } from "../../../utils/isSpecificSite";
import WrappedSection from "../../WrappedSection/WrappedSection";
import { DogBioField, type DogBioFieldProps } from "./SectionDogBio.Components";

const DOG_TEXT =
	"I'm looking for my forever home. Could you be my perfect match?";

const LISTING_BASE_URL = "/rehoming/dogs";

export interface SectionDogBioProps {
	data: DogsTrust.DogData;
	centreName: string;
	userLocation: DogsTrust.Place;
	adoptionFormUrl: string;
}
export const SectionDogBio: React.FC<SectionDogBioProps> = ({
	data,
	centreName,
	userLocation,
	adoptionFormUrl,
}) => {
	isSpecificSite("uk", "SectionDogBio");

	const { windowLocationHref } = useWindowLocationHref();
	const { useDogSearchQuery, onChangeFilterView } = useDogSearchApi();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		useDogSearchQuery();
	}, []);

	const buildCanLiveWithStringBuilder = () => {
		const {
			name,
			liveWithCats,
			liveWithDogs,
			liveWithPreschool,
			liveWithPrimary,
			liveWithSecondary,
		} = data;

		const getLiveWithAnimals = () => {
			const catsLink = (
				<span className={styles.value}>
					<Link to={`${LISTING_BASE_URL}?page=0&sort=A&liveWithCats=true`}>
						Cats
					</Link>
				</span>
			);
			const dogsLink = (
				<span className={styles.value}>
					<Link to={`${LISTING_BASE_URL}?page=0&sort=A&liveWithDogs=true`}>
						Dogs
					</Link>
				</span>
			);

			if (!liveWithCats && !liveWithDogs) return null;
			// Cats but not dogs
			if (liveWithCats && !liveWithDogs) return catsLink;
			// Dogs but not cats
			if (!liveWithCats && liveWithDogs) return dogsLink;

			// Cats and dogs
			return (
				<>
					{catsLink}
					{" and "}
					{dogsLink}
				</>
			);
		};

		const getLiveWithChildren = () => {
			const canLiveWithChildren = [];

			if (liveWithPreschool)
				canLiveWithChildren.push(
					<span className={styles.value}>
						<Link
							to={`${LISTING_BASE_URL}?page=0&sort=A&liveWithPreschool=true`}
						>
							Preschool
						</Link>
					</span>,
				);
			if (liveWithPrimary)
				canLiveWithChildren.push(
					<span className={styles.value}>
						<Link to={`${LISTING_BASE_URL}?page=0&sort=A&liveWithPrimary=true`}>
							Primary
						</Link>
					</span>,
				);
			if (liveWithSecondary)
				canLiveWithChildren.push(
					<span className={styles.value}>
						<Link
							to={`${LISTING_BASE_URL}?page=0&sort=A&liveWithSecondary=true`}
						>
							Secondary
						</Link>
					</span>,
				);

			// Can not live with any children
			if (!canLiveWithChildren.length) return null;

			// Can live with all children;
			return (
				<>
					{canLiveWithChildren.map((x, i) => {
						// There is more items so add "&"
						if (i + 1 < canLiveWithChildren.length)
							return (
								<span key={`${x.props?.title}-${i}`}>
									{x} <span>{"&"} </span>
								</span>
							);
						// This is the last one so finish the sentence
						if (i + 1 === canLiveWithChildren.length)
							return (
								<span key={`${x.props?.title}-${i}`}>
									{x} <span>school age children.</span>
								</span>
							);
					})}
				</>
			);
		};

		const liveWithAnimals = getLiveWithAnimals();
		const liveWithChildren = getLiveWithChildren();

		if (liveWithAnimals === null && liveWithChildren === null)
			return (
				<span>{`${name} needs a home with no other pets or children.`}</span>
			);
		if (liveWithChildren === null) return <div>{liveWithAnimals}</div>;
		if (liveWithAnimals === null) return <div>{liveWithChildren}</div>;

		return (
			<div>
				{liveWithAnimals}
				<span> and </span>
				{liveWithChildren}
			</div>
		);
	};

	const userLocationString = userLocation
		? QueryString.stringify(userLocation)
		: "";

	const breed: DogBioFieldProps = {
		icon: Breed,
		title: "Breed",
		value: data.frontEndBreedName,
		url: `${LISTING_BASE_URL}?breed%5B0%5D=${encodeURI(
			data.breed,
		)}&${userLocationString}`,
	};
	const referenceId: DogBioFieldProps = {
		icon: Reference,
		title: "Reference ID",
		value: data.key.toString(),
		url: "",
	};
	const age: DogBioFieldProps = {
		icon: Age,
		title: "Age",
		value: getDogAgeBand(data.dob),
		url: `${LISTING_BASE_URL}?age%5B0%5D=${encodeURI(
			getDogAgeBand(data.dob),
		)}&${userLocationString}`,
	};
	const sex: DogBioFieldProps = {
		icon: Sex,
		title: "Sex",
		value: data.gender === "M" ? "Male" : "Female",
		url: `${LISTING_BASE_URL}?gender%5B0%5D=${encodeURI(
			data.gender === "M" ? "Male" : "Female",
		)}`,
	};
	const size: DogBioFieldProps = {
		icon: Measurements,
		title: "Size",
		value: data.size,
		url: `${LISTING_BASE_URL}?size%5B0%5D=${encodeURI(
			data.size,
		)}&${userLocationString}`,
		ExtraField: () => (
			<div className={styles.sizeguide}>
				<button
					type="button"
					className={styles.sizeguidecontent}
					onClick={() => onChangeFilterView("SizeGuide")}
					onKeyDown={(e) =>
						e.key === "Enter" && onChangeFilterView("SizeGuide")
					}
				>
					Size Guide
					<Info />
				</button>
			</div>
		),
	};
	const centre: DogBioFieldProps = {
		icon: Location,
		title: "Location",
		value: centreName,
		url: `${LISTING_BASE_URL}?centres%5B0%5D=${encodeURI(data.centreCode)}`,
	};
	const canLiveWith: DogBioFieldProps = {
		icon: Home,
		title: "May live with",
		value: buildCanLiveWithStringBuilder(),
	};
	const needsMedical: DogBioFieldProps = {
		icon: Medical,
		title: "Medical care",
		value: "I need ongoing medical care",
	};
	const homeFromHome: DogBioFieldProps = {
		icon: OffSite,
		title: "Living off site",
		value: "Yes",
	};

	const firstSection = [
		breed,
		referenceId,
		age,
		sex,
		data.needsMedicalCare ? size : null,
		data.needsMedicalCare ? needsMedical : null,
		data.isHomeFromHome ? centre : null,
		data.isHomeFromHome ? homeFromHome : null,
	].filter((x) => !!x);
	const secondSection = [
		!data.isHomeFromHome ? centre : null,
		canLiveWith,
		!data.needsMedicalCare ? size : null,
	].filter((x) => !!x);

	// live with as a simple string for tracking purposes - a html stripped out version
	const liveWithSimpleString = () => {
		const htmlString = renderToString(buildCanLiveWithStringBuilder());
		const htmlCleanedstring = htmlString.replace(/<[^>]+>/g, "");
		return Html5Entities.decode(htmlCleanedstring);
	};

	return (
		<WrappedSection
			sectionName="SectionDogBio"
			sectionColour={Colours.SANDY_FUR}
		>
			<div className={styles.dogbioinner}>
				<div
					className={
						data.isUnderdog
							? styles.headingsectionwithlabel
							: styles.headingsection
					}
				>
					{data.isReserved && (
						<div className={styles.dogisreserved}>
							<Star
								className={styles.tagicon}
								height="14px"
								width="14px"
								fill={"black"}
							/>
							RESERVED
						</div>
					)}
					{data.isUnderdog && !data.isReserved && (
						<div className={styles.dogisunderdog}>
							<Home
								className={styles.tagicon}
								height="14px"
								width="14px"
								fill={Colours.MIDNIGHT}
							/>
							UNDERDOG
						</div>
					)}
					<div className={styles.text}>
						<h1>{data.name}</h1>
						<p>{DOG_TEXT}</p>
					</div>
					<div className={styles.favourite}>
						<FavouriteIcon
							dog={data}
							size={"medium"}
							dogData={{
								event: "rehoming_favourite",
								rehoming_name: data.name, // name of dog added to favourites
								rehoming_location: centreName, // location of dog added to favourites
								rehoming_breed: data.frontEndBreedName, // dog breed of dog added to favourites
								rehoming_age: getDogAgeBand(data.dob), // dog age of dog added to favourites
								rehoming_size: data.size, // dog size of dog added to favourites
								rehoming_gender: data.gender === "M" ? "Male" : "Female", // gender
								rehoming_live_with: liveWithSimpleString(), // may live with preferences
							}}
						/>
					</div>
				</div>
				<div className={styles.contentsection}>
					<div className={styles.firstsection}>
						<div className={styles.dogbiofields}>
							<div className={styles.dogbiofieldsfirst}>
								{firstSection.map((field, i) => (
									<DogBioField key={`${field.title}-${i}`} {...field} />
								))}
							</div>
							<div className={styles.dogbiofieldssecond}>
								{secondSection.map((field, i) => (
									<DogBioField key={`${field.title}-${i}`} {...field} />
								))}
							</div>
						</div>
					</div>
					<div className={styles.secondsection}>
						<DogBioLinks
							{...data}
							dog={data}
							id={data.key.toString()}
							adoptionFormUrl={adoptionFormUrl}
							xUrl={encodeURI(shareOnX(windowLocationHref, data.name))}
							facebookUrl={encodeURI(shareOnFB(windowLocationHref, data.name))}
							mailToUrl={encodeURI(
								shareViaEmail(windowLocationHref, data.name, data.breed),
							)}
							whatsAppUrl={encodeURI(
								shareViaWhatsApp(windowLocationHref, data.name, data.breed),
							)}
							dogDataTracking={{
								rehoming_name: data.name, // name of dog added to favourites
								rehoming_location: centreName, // location of dog added to favourites
								rehoming_breed: data.frontEndBreedName, // dog breed of dog added to favourites
								rehoming_age: getDogAgeBand(data.dob), // dog age of dog added to favourites
								rehoming_size: data.size, // dog size of dog added to favourites
								rehoming_gender: data.gender === "M" ? "Male" : "Female", // gender
								rehoming_live_with: liveWithSimpleString(), // may live with preferences
							}}
						/>
					</div>
				</div>
			</div>
		</WrappedSection>
	);
};

export default SectionDogBio;
